import { EllipsedText, FlexColumn, FlexRow, MaterialIcon } from '@gorila-shared-ui/components';
import { NotificationCircle } from 'baseui/badge';
import { Button, ButtonOverrides } from 'baseui/button';
import { Items, StatefulMenu } from 'baseui/menu';
import { StatefulPopover } from 'baseui/popover';
import { LabelSmall, ParagraphXSmall } from 'baseui/typography';
import { memo, useMemo } from 'react';
import { generatePath } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
  ASSET_INFO_ACTION,
  HISTORIC_REPORT_ACTION,
  SECONDARIES_ACTION,
  SECONDARIES_PAGE,
  SEND_COMMAND_ACTION,
} from '../../constants/app';
import { ASSET_TYPE_PHONE } from '../../constants/asset';
import { useStyles } from '../../hooks/useStyles';
import { apiParamsState, availableActionsState, isPublicPageState } from '../../storage/app';
import { selectedCurrentStateActionState } from '../../storage/client';
import { CurrentState, CurrentStateAction } from '../../types/currentState';
import { DeviceGpsType } from '../../types/device';
import { getAssetTitle } from '../../utils/assets';
import { deviceIsCamera, isDevice } from '../../utils/currentStates';
import OperationalStatusLabel from '../shared/OperationalStatusLabel';
import OperationalStatusTime from '../shared/OperationalStatusTime';
import { StyledTooltip } from '../ui/StyledTooltip';
import { StreamingMenu } from './streaming/StreamingMenu';

type Props = {
  currentState: CurrentState;
};

function CurrentStateCardHeader({ currentState }: Readonly<Props>) {
  const { theme, css, classes } = useStyles();
  const isPublicPage = useRecoilValue(isPublicPageState);
  const actions = useRecoilValue(availableActionsState);
  const apiParams = useRecoilValue(apiParamsState);
  const setAction = useSetRecoilState(selectedCurrentStateActionState);
  const tooltipPlacement = 'bottom';
  const miniButtonOverrides: ButtonOverrides = {
    BaseButton: {
      style: {
        width: '24px',
        height: '24px',
      },
    },
  };
  const requiresAttention = currentState.requiresAttention !== undefined && currentState.requiresAttention > 0;
  const isObd = isDevice(currentState.asset.device) && currentState.deviceGpsType === DeviceGpsType.obd;
  const isCamera = deviceIsCamera(currentState);
  const { asset } = currentState;

  const onActionSelect = (action: CurrentStateAction) => {
    setAction({ ident: currentState.ident, name: action });
  };

  const onItemSelect = async (item: any, close: () => void) => {
    if (item.id === 'secondaries' && apiParams) {
      window.open(
        generatePath(SECONDARIES_PAGE.route, {
          id: apiParams.subClientId,
          assetId: currentState.asset._id,
        }),
        '_blank',
        'directories=no,titlebar=no,toolbar=no,location=no,status=no,menubar=no,scrollbars=no,resizable=no, height=700px, width=1400px',
      );
    } else if (item.id === 'info') {
      onActionSelect(CurrentStateAction.info);
    } else if (item.id === 'historic') {
      onActionSelect(CurrentStateAction.historicReport);
    } else if (item.id === 'commands') {
      onActionSelect(CurrentStateAction.command);
    } else if (item.id === 'audioCommands') {
      onActionSelect(CurrentStateAction.audioCommand);
    }
    close();
  };

  const openDtc = () => {
    onActionSelect(CurrentStateAction.dtcList);
  };

  const moreOptions: Items | undefined = useMemo(() => {
    const options = [];

    if (actions.includes(SECONDARIES_ACTION) && !!currentState.asset.secondaries?.length) {
      options.push({
        id: 'secondaries',
        label: (
          <FlexRow
            classNames={classes.centeredStart}
            gap={theme.sizing.scale400}
          >
            <MaterialIcon name="devices_other" />
            Ver dispositivos secundarios
          </FlexRow>
        ),
      });
    }
    if (actions.includes(ASSET_INFO_ACTION)) {
      options.push({
        id: 'info',
        label: (
          <FlexRow
            classNames={classes.centeredStart}
            gap={theme.sizing.scale400}
          >
            <MaterialIcon name="info" />
            Información del activo
          </FlexRow>
        ),
      });
    }
    if (actions.includes(HISTORIC_REPORT_ACTION)) {
      options.push({
        id: 'historic',
        label: (
          <FlexRow
            classNames={classes.centeredStart}
            gap={theme.sizing.scale400}
          >
            <MaterialIcon name="timeline" />
            Solicitar histórico de posiciones
          </FlexRow>
        ),
      });
    }
    if (actions.includes(SEND_COMMAND_ACTION) && isCamera) {
      options.push({
        id: 'audioCommands',
        label: (
          <FlexRow
            classNames={classes.centeredStart}
            gap={theme.sizing.scale400}
          >
            <MaterialIcon name="campaign" />
            Enviar Indicación
          </FlexRow>
        ),
      });
    }
    if (actions.includes(SEND_COMMAND_ACTION) && currentState.asset.assetType !== ASSET_TYPE_PHONE) {
      options.push({
        id: 'commands',
        label: (
          <FlexRow
            classNames={classes.centeredStart}
            gap={theme.sizing.scale400}
          >
            <MaterialIcon name="settings_remote" />
            Enviar comando
          </FlexRow>
        ),
      });
    }

    return options.length ? options : undefined;
  }, [actions, currentState.asset.assetType, currentState.asset.secondaries?.length]);

  return (
    <FlexRow
      classNames={css({
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: `${theme.sizing.scale300} ${theme.sizing.scale600}`,
        overflow: 'hidden',
      })}
    >
      <FlexRow
        gap={theme.sizing.scale300}
        classNames={classes.centeredStart}
      >
        <FlexColumn gap={0}>
          <LabelSmall>
            <EllipsedText
              breakAll
              inline
            >
              {getAssetTitle(currentState.asset)}
            </EllipsedText>
          </LabelSmall>
          <StyledTooltip
            content="Etiqueta"
            placement={tooltipPlacement}
          >
            <ParagraphXSmall
              margin={0}
              color={theme.colors.contentSecondary}
            >
              <EllipsedText
                breakAll
                inline
              >
                {`${currentState.asset.label}`}
              </EllipsedText>
            </ParagraphXSmall>
          </StyledTooltip>
        </FlexColumn>
        {!isPublicPage && isCamera && <StreamingMenu currentState={currentState} />}
      </FlexRow>
      <FlexRow
        gap={theme.sizing.scale300}
        classNames={classes.centeredStart}
      >
        {!isPublicPage && isObd && (
          <StyledTooltip
            content="Registro de fallas"
            placement={tooltipPlacement}
            showArrow={false}
          >
            <Button
              size="mini"
              shape="square"
              onClick={openDtc}
              kind="tertiary"
              overrides={miniButtonOverrides}
            >
              <NotificationCircle
                content={currentState.canDtc?.length}
                hidden={!currentState.canDtc?.length}
                horizontalOffset="-7px"
                verticalOffset="-7px"
                overrides={{
                  Badge: {
                    style: {
                      backgroundColor: theme.colors.negative,
                      fontSize: '9px',
                      width: '15px',
                      height: '15px',
                    },
                  },
                }}
              >
                <MaterialIcon
                  name="notifications_important"
                  size="20px"
                  color={requiresAttention ? theme.colors.negative : theme.colors.contentTertiary}
                />
              </NotificationCircle>
            </Button>
          </StyledTooltip>
        )}
        {!!asset.isDoorOpen && (
          <StyledTooltip
            content="Puerta abierta"
            placement={tooltipPlacement}
            showArrow={false}
          >
            <MaterialIcon
              name="car_door"
              color={theme.colors.negative}
            />
          </StyledTooltip>
        )}
        {!!asset.isEngineStall && (
          <StyledTooltip
            content="Paro de motor"
            placement={tooltipPlacement}
            showArrow={false}
          >
            <MaterialIcon
              name="engine_stop"
              warn
            />
          </StyledTooltip>
        )}
        {currentState.operationalState && <OperationalStatusTime operationalState={currentState.operationalState} />}
        {currentState.operationalState && (
          <OperationalStatusLabel
            operationalState={currentState.operationalState}
            positionDirection={currentState.position_direction}
            speed={currentState.position_speed}
            sleepMode={currentState.sleepModeStatus}
          />
        )}
        {!isPublicPage && moreOptions && (
          <StatefulPopover
            content={({ close }) => (
              <StatefulMenu
                onItemSelect={({ item }) => onItemSelect(item, close)}
                items={moreOptions}
              />
            )}
            placement={'bottomRight'}
            triggerType={'click'}
          >
            <span className={css({ lineHeight: 1 })}>
              <Button
                size="mini"
                kind="tertiary"
                shape="square"
                overrides={miniButtonOverrides}
              >
                <MaterialIcon
                  name="more_vert"
                  size="18px"
                />
              </Button>
            </span>
          </StatefulPopover>
        )}
      </FlexRow>
    </FlexRow>
  );
}

export default memo(CurrentStateCardHeader);
